// =====================================================
// INFORMACIÓN SATELITAL POR LOTE
// Componente que unifica la información satelital a nivel de lote
// src/pages/Agro/Establecimiento/InformacionSatelitalLote.js
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  FormGroup,
  Label,
  Button,
} from "reactstrap"
import classnames from "classnames"
import { Map, TileLayer, Polygon, Popup } from "react-leaflet"
import "leaflet/dist/leaflet.css"

import { getPoligonosByEstablecimiento } from "api/agro"
// Importar componentes existentes adaptados para lote
import VisorImagenesSatelitalesLote from "./VisorImagenesSatelitalesLote"
import DatosSueloLote from "./DatosSueloLote"
import GraficoNdviLote from "./GraficoNdviLote"
import toastHandler from "toastHandler"
import { estiloLote, etiquetaTenencia } from "constants/usosLote"
import PatronesLote from "components/Mapa/PatronesLote"

// =====================================================
// COMPONENTE: SELECTOR DE LOTE
// =====================================================

const SelectorLote = ({ poligonos, loteSeleccionado, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" className="me-2" />
        <span className="text-muted">Cargando lotes...</span>
      </div>
    )
  }

  if (poligonos.length === 0) {
    return (
      <Alert color="warning" className="mb-0">
        <i className="mdi mdi-alert me-2" />
        <strong>No hay lotes definidos</strong>
        <p className="mb-0 mt-2 small">
          Para ver información satelital, primero defina los lotes en
          Polígonos/Lotes.
        </p>
      </Alert>
    )
  }

  return (
    <Card className="mb-3 border-success" style={{ borderWidth: "2px" }}>
      <CardBody
        className="py-3"
        style={{
          background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
        }}
      >
        <Row className="align-items-center">
          <Col md={4}>
            <FormGroup className="mb-0">
              <Label className="fw-bold text-success mb-1">
                <i className="mdi mdi-map-marker me-1" />
                Seleccionar Lote
              </Label>
              <Input
                type="select"
                value={loteSeleccionado?.id || ""}
                onChange={e => {
                  const lote = poligonos.find(
                    p => p.id.toString() === e.target.value,
                  )
                  onSelect(lote)
                }}
                bsSize="sm"
              >
                <option value="">-- Seleccione un lote --</option>
                {poligonos.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nombreLote || p.nombre || `Lote ${p.id}`}
                    {p.superficieHa ? ` - ${p.superficieHa} ha` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          {loteSeleccionado && (
            <Col md={8}>
              <div className="d-flex gap-3 flex-wrap justify-content-end">
                <div className="bg-white rounded px-3 py-2 shadow-sm">
                  <small className="text-muted d-block">Superficie</small>
                  <strong className="text-primary">
                    {loteSeleccionado.superficieHa?.toFixed(2) || "-"} ha
                  </strong>
                </div>
                <div className="bg-white rounded px-3 py-2 shadow-sm">
                  <small className="text-muted d-block">Uso Actual</small>
                  <Badge color="secondary">
                    {loteSeleccionado.usoActual || "-"}
                  </Badge>
                </div>
                {/* BDSJEA-463: tenencia del lote (también en texto: la trama del mapa no es el único canal) */}
                <div className="bg-white rounded px-3 py-2 shadow-sm">
                  <small className="text-muted d-block">Tenencia</small>
                  <Badge color="info">
                    {etiquetaTenencia(loteSeleccionado.tipoTenencia)}
                  </Badge>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </CardBody>
    </Card>
  )
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const InformacionSatelitalLote = ({ idEstablecimiento }) => {
  const [loading, setLoading] = useState(true)
  const [poligonos, setPoligonos] = useState([])
  const [loteSeleccionado, setLoteSeleccionado] = useState(null)
  const [activeTab, setActiveTab] = useState("imagenes")
  const [mostrarMapaExpandido, setMostrarMapaExpandido] = useState(false)

  const setError = toastHandler(state => state.setError)

  // Parsear GeoJSON del lote seleccionado
  const getCoordenadasPoligono = poligono => {
    if (!poligono?.geojson) return null
    try {
      const geojson = JSON.parse(poligono.geojson)
      let coords = []
      if (geojson.type === "Feature" && geojson.geometry) {
        coords = geojson.geometry.coordinates[0]
      } else if (geojson.type === "Polygon") {
        coords = geojson.coordinates[0]
      }
      if (!coords || !coords.length) return null
      return coords.map(coord => [coord[1], coord[0]])
    } catch (e) {
      console.error("Error parseando GeoJSON:", e)
      return null
    }
  }

  const coordenadasPoligono = getCoordenadasPoligono(loteSeleccionado)
  // BDSJEA-463: el estilo del lote seleccionado se calcula una vez por render
  // (el lote por defecto sigue gris con borde discontinuo: es intencional, igual que en el editor de lotes)
  const estiloSeleccionado = estiloLote(loteSeleccionado || {})

  useEffect(() => {
    if (idEstablecimiento) {
      loadPoligonos()
    }
  }, [idEstablecimiento])

  const loadPoligonos = async () => {
    setLoading(true)
    try {
      const data = await getPoligonosByEstablecimiento(idEstablecimiento)
      setPoligonos(data || [])
      if (data && data.length > 0) {
        setLoteSeleccionado(data[0])
      }
    } catch (error) {
      console.error("Error cargando polígonos:", error)
      setError("Error al cargar los lotes")
    } finally {
      setLoading(false)
    }
  }

  if (!loading && poligonos.length === 0) {
    return (
      <Alert color="warning">
        <i className="mdi mdi-alert me-2" />
        <strong>No hay lotes definidos</strong>
        <p className="mb-0 mt-2">
          Para ver información satelital, primero defina los lotes en
          Polígonos/Lotes.
        </p>
      </Alert>
    )
  }

  return (
    <div>
      <SelectorLote
        poligonos={poligonos}
        loteSeleccionado={loteSeleccionado}
        onSelect={setLoteSeleccionado}
        loading={loading}
      />

      {loteSeleccionado && (
        <>
          <Card className="mb-3 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  <i className="mdi mdi-map-marker me-1" />
                  Polígono del lote
                </h6>
              </div>
              <div
                style={{
                  position: "relative",
                  height: mostrarMapaExpandido ? 500 : 200,
                }}
              >
                {/* BDSJEA-463: mismas tramas que el editor de lotes (punteado = propio, rayado = arrendado) */}
                <PatronesLote />
                <Map
                  center={coordenadasPoligono[0]}
                  zoom={15}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Esri"
                  />
                  <Polygon
                    positions={coordenadasPoligono}
                    color={estiloSeleccionado.color}
                    fillColor={estiloSeleccionado.fillColor}
                    fillOpacity={estiloSeleccionado.fillOpacity}
                    weight={3}
                  >
                    <Popup>
                      <strong>
                        {loteSeleccionado.nombreLote ||
                          `Lote ${loteSeleccionado.id}`}
                      </strong>
                      <br />
                      Superficie: {loteSeleccionado.superficieHa} ha
                      <br />
                      Uso: {loteSeleccionado.usoActual}
                      <br />
                      Tenencia: {estiloSeleccionado.etiquetaTenencia}
                    </Popup>
                  </Polygon>
                </Map>
                {loteSeleccionado?.geojson && (
                  <Button
                    color="light"
                    size="sm"
                    onClick={() =>
                      setMostrarMapaExpandido(!mostrarMapaExpandido)
                    }
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1000,
                    }}
                  >
                    <i
                      className={`mdi ${mostrarMapaExpandido ? "mdi-chevron-up" : "mdi-chevron-down"} me-1`}
                    />
                    {mostrarMapaExpandido ? "Contraer vista" : "Expandir vista"}
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>

          <style>
            {`
              .nav-tabs-satelital .nav-link.active {
                color: #1b5e20 !important;
                background-color: #fff !important;
                font-weight: 600 !important;
                border-bottom: 3px solid #43a047 !important;
              }
              .nav-tabs-satelital .nav-link { color: #6c757d !important; cursor: pointer; }
              .nav-tabs-satelital .nav-link:hover:not(.active) { color: #1b5e20 !important; }
            `}
          </style>

          <Nav tabs className="nav-tabs-satelital mb-3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "imagenes" })}
                onClick={() => setActiveTab("imagenes")}
              >
                <i className="mdi mdi-satellite-variant me-1" />
                Imágenes Satelitales
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "suelo" })}
                onClick={() => setActiveTab("suelo")}
              >
                <i className="mdi mdi-image-filter-hdr me-1" />
                Datos de Suelo
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "ndvi" })}
                onClick={() => setActiveTab("ndvi")}
              >
                <i className="mdi mdi-leaf me-1" />
                Índice NDVI
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="imagenes">
              <VisorImagenesSatelitalesLote
                idEstablecimiento={idEstablecimiento}
                poligono={loteSeleccionado}
              />
            </TabPane>
            <TabPane tabId="suelo">
              <DatosSueloLote
                idPoligono={loteSeleccionado.id}
                poligono={loteSeleccionado}
              />
            </TabPane>
            <TabPane tabId="ndvi">
              <GraficoNdviLote
                idPoligono={loteSeleccionado.id}
                poligono={loteSeleccionado}
              />
            </TabPane>
          </TabContent>
        </>
      )}
    </div>
  )
}

export default InformacionSatelitalLote
